/* eslint-disable react-hooks/exhaustive-deps */
import { useSearch } from '@faststore/sdk'
import { useEffect, useState } from 'react'
import StockAvailable from 'src/components/common/StockAvailable/StockAvailable'
import { useCmsPlpQuery } from 'src/components/hooks/useCmsPlpQuery'
import { useFilteredFacets } from 'src/components/hooks/useFilteredFacets'
import Filter from 'src/components/search/Filter'
import Sort from 'src/components/search/Sort'
import FilterSkeleton from 'src/components/skeletons/FilterSkeleton'
import SkeletonElement from 'src/components/skeletons/SkeletonElement'
import Button from 'src/components/ui/Button'
import Icon from 'src/components/ui/Icon'
import { ITEMS_PER_PAGE } from 'src/constants'
import { mark } from 'src/sdk/tests/mark'
import { useUI } from 'src/sdk/ui/Provider'
import { filterAvailableShelfProducts } from 'src/utils/filterAvailableProducts'
import {
  getSessionPaginationInfo,
  setSessionPaginationInfo,
} from 'src/utils/getSessionPaginarionInfo'

import SearchBanners from '../SearchBanners/SearchBanners'
import Section from '../Section'
import BannerPlp from './BannerPlp'
import CarrouselPlp from './CarouselPlp'
import EmptyGallery from './EmptyGallery'
import GalleryPages from './GalleryPages'
import LinkToNextPage from './LinkToNextPage'
import styles from './product-gallery.module.scss'
import { useDelayedFacets } from './useDelayedFacets'
import { useGalleryQuery } from './useGalleryQuery'
import InsiderEventCategory from 'src/components/InsiderEvent/EventCategory'

type SearchBannersType = {
  area: string
  html: string
  id: string
  name: string
}

interface Props {
  title: string
  searchTerm?: string
  collectionName?: string
  useFilters: boolean
  searchBanners?: {
    banners: SearchBannersType[]
  }
  slug?: string
}

function ProductGallery({
  title,
  searchTerm,
  collectionName,
  useFilters,
  searchBanners,
  slug,
}: Props) {
  const cmsPlp = useCmsPlpQuery()

  const { data } = useGalleryQuery()

  const { openFilter } = useUI()

  const facets = useDelayedFacets(data)
  const facetsValues = useFilteredFacets(facets)
  const loadingFacets = facets?.length === 0

  const availableProducts = filterAvailableShelfProducts(data?.search?.products)

  const totalCount = Number(data?.search.products.pageInfo.totalCount) ?? 0
  const filteredProducts = data?.search.products.pageInfo.totalCount ?? 0

  const searchPageTitle = searchTerm ?? collectionName

  const [sessionPage, setSessionPage] = useState<number>(1)
  const { itemsPerPage, state, resetInfiniteScroll } = useSearch()
  const sessionPageStorage = getSessionPaginationInfo()
  const sessionPathName = searchTerm ?? state.base

  // This useEffect is to get the correct pagination and scroll position based on the session storage
  useEffect(() => {
    if (!sessionPageStorage) {
      return
    }

    if (sessionPageStorage?.sessionPath !== sessionPathName) {
      resetInfiniteScroll(0)

      return setSessionPaginationInfo(1, sessionPathName, 0)
    }

    if (sessionPageStorage?.sessionPath === sessionPathName) {
      window.scrollTo(0, sessionPageStorage.sessionScrollPosition)

      return setSessionPage(sessionPageStorage.sessionPage)
    }
  }, [sessionPageStorage])

  if (
    (data && filteredProducts === 0) ||
    (data && availableProducts?.length === 0)
  ) {
    return (
      <Section
        data-testid="product-gallery"
        className={`${styles.fsProductListing} layout__content`}
        data-fs-product-listing
      >
        <EmptyGallery />
        <div data-fs-screen-empty-stock-available>
          <StockAvailable icon />
        </div>
      </Section>
    )
  }

  return (
    <>
      <InsiderEventCategory
        collectionName={collectionName}
        searchTerm={searchTerm}
      />
      <Section
        data-testid="product-gallery"
        className={`${styles.fsProductListing} layout__content-full`}
        data-fs-product-listing
      >
        {useFilters ? (
          <div
            data-fs-product-listing-content-grid
            data-view-categories={!searchTerm}
            className="layout__content"
          >
            <div data-fs-product-listing-filters>
              <FilterSkeleton loading={loadingFacets}>
                <Filter facets={facetsValues} />
              </FilterSkeleton>
            </div>

            <Section data-banner-products>
              <SearchBanners searchBanners={searchBanners} area="one" />
              <div data-promotional-banner>
                <BannerPlp
                  className="plpBanner"
                  valueLoading="eager"
                  promotionalBannerData={cmsPlp}
                  imageWidth={{
                    desktop: 1040,
                    mobile: 328,
                  }}
                  imageHeight={{
                    desktop: 158,
                    mobile: 94,
                  }}
                />
              </div>

              <header
                data-fs-product-listing-search-term
                className="layout__content"
              >
                <h1>
                  <span>{searchPageTitle}</span>
                </h1>
              </header>

              <div className={styles.carouselplp__container}>
                <CarrouselPlp slug={slug} cmsPlpData={cmsPlp} />
              </div>

              <div data-fs-product-listing className="product-listing__space">
                <div
                  data-fs-product-listing-results-count
                  data-count={totalCount}
                >
                  <SkeletonElement
                    shimmer
                    type="text"
                    loading={availableProducts === undefined}
                  >
                    <h2 data-testid="total-product-count">
                      {availableProducts?.length === 0
                        ? 'No Hay Productos'
                        : ''}
                    </h2>
                  </SkeletonElement>
                </div>

                <div data-fs-product-listing-sort>
                  <SkeletonElement shimmer type="text" loading={loadingFacets}>
                    <Sort />
                  </SkeletonElement>

                  <SkeletonElement
                    shimmer
                    type="button"
                    loading={loadingFacets}
                  >
                    <Button
                      className="plp__filter-by-button"
                      variant="tertiary"
                      data-testid="open-filter-button"
                      icon={<Icon name="FilterFunnel" width={18} height={16} />}
                      iconPosition="left"
                      aria-label="Open Filters"
                      onClick={openFilter}
                    >
                      Filtrar
                    </Button>
                  </SkeletonElement>
                </div>
              </div>
              <div
                data-fs-product-listing-results
                className="plp__shelf-products-container"
              >
                {/* Render ALL products */}
                <GalleryPages
                  data={data}
                  title={title}
                  itemsPerPage={itemsPerPage}
                  sessionPage={sessionPage}
                />
                {/* Add link to next page. This helps on SEO */}
                {sessionPage * ITEMS_PER_PAGE <= totalCount && (
                  <LinkToNextPage
                    totalCount={totalCount}
                    setSessionPage={setSessionPage}
                    sessionPathName={sessionPathName}
                    sessionPage={sessionPage}
                  />
                )}
                <SearchBanners searchBanners={searchBanners} area="two" />
                <StockAvailable icon className="plp__stock-available" />
              </div>
            </Section>
          </div>
        ) : (
          <div
            data-fs-product-listing-content-grid-no-filter
            data-view-categories={!searchTerm}
            className="layout__content"
          >
            <Section data-banner-products-lp>
              <div data-fs-product-title>{title}</div>
              <div
                data-fs-product-listing-lp
                className="plp__shelf-products-container"
              >
                {/* Render ALL products */}
                <GalleryPages
                  data={data}
                  title={title}
                  itemsPerPage={itemsPerPage}
                  sessionPage={sessionPage}
                />
                {/* Add link to next page. This helps on SEO */}
                {sessionPage * ITEMS_PER_PAGE <= totalCount && (
                  <LinkToNextPage
                    totalCount={totalCount}
                    setSessionPage={setSessionPage}
                    sessionPathName={sessionPathName}
                    sessionPage={sessionPage}
                  />
                )}
              </div>
            </Section>
          </div>
        )}
      </Section>
    </>
  )
}

ProductGallery.displayName = 'ProductGallery'
export default mark(ProductGallery)
